import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { cloneElement, isValidElement, type ReactElement, type ReactNode, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SettingsForm } from '@/components/Settings/SettingsForm';
import { settingsDefaults, settingsSchema } from '@/components/Settings/SettingsForm.schema';
import { Button } from '@/components/Ui/Button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/Ui/Dialog';
import { StandardButtons } from '@/components/Ui/Form/Rhf/StandardButtons';
import { useQuote } from '@/contexts/QuoteContext';
import auth, { type User } from '@/helpers/auth';
import { overwrite } from '@/helpers/utils';
import { useEntityForm } from '@/hooks/useEntityForm';

interface SettingsDialogProps {
    trigger?: ReactNode;
    onChange?: () => void;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export function SettingsDialog({ trigger = null, onChange, open: controlledOpen, onOpenChange }: SettingsDialogProps) {
    const [
        t,
    ] = useTranslation('pages');
    const { clearQuote } = useQuote();
    const [
        internalOpen,
        setInternalOpen,
    ] = useState(false);

    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = isControlled ? (onOpenChange ?? setInternalOpen) : setInternalOpen;

    const entity = auth.getUser();
    const initialValues = overwrite(settingsDefaults(), entity);
    const schema = settingsSchema({ isCreate: false, t });

    const handleSave = async (values: Record<string, unknown>) => {
        const prevPriceDisplay = auth.getUser()?.price_display;
        const { data } = await axios.patch<User>('/api/profile', values);

        auth.setUser(data);

        if (data.price_display !== prevPriceDisplay) {
            clearQuote();
        }

        if ('active_workspace_id' in values || 'locale' in values) {
            window.dispatchEvent(new CustomEvent('session-changed'));
        } else if (onChange) {
            onChange();
        }
    };

    const { form, onSubmit, saveError } = useEntityForm({
        schema,
        initialValues,
        isCreateMode: false,
        onSave: handleSave,
        open,
        resetKey: entity?.uuid,
        onClose: () => {
            return setOpen(false);
        },
    });

    const defaultTrigger = (
        <Button className='px-0!' size='sm' variant='ghost'>
            <Cog6ToothIcon className='h-4 w-4 mr-2' />
            <span className='hidden lg:block'>{ t('Settings') }</span>
        </Button>
    );

    const triggerElement = trigger || defaultTrigger;
    const shouldRenderTrigger = !isControlled && triggerElement;

    return (
        <Dialog onOpenChange={ setOpen } open={ open }>
            { shouldRenderTrigger && (
                isValidElement(triggerElement)
                    ? cloneElement(triggerElement as ReactElement<{ onClick?: () => void }>, { onClick: () => {
                        return setOpen(true);
                    } })
                    : triggerElement
            ) }
            <FormProvider { ...form }>
                <DialogContent className='sm:max-w-[500px] lg:max-w-[964px] '>
                    <form className='contents' onSubmit={ form.handleSubmit(onSubmit) }>
                        <DialogHeader>
                            <DialogTitle>{ t('Account Settings') }</DialogTitle>
                            <DialogDescription>
                                { t('Manage your profile information.') }
                            </DialogDescription>
                        </DialogHeader>

                        <div className='flex-1 overflow-y-auto px-6 py-4 space-y-4'>
                            { saveError && (
                                <div className='text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded px-3 py-2'>
                                    { saveError }
                                </div>
                            ) }
                            <SettingsForm />
                        </div>

                        <DialogFooter>
                            <StandardButtons
                                onCancel={ () => {
                                    return setOpen(false);
                                } }
                            />
                        </DialogFooter>
                    </form>
                </DialogContent>
            </FormProvider>
        </Dialog>
    );
}
